import { useRequiredContext } from "@redotech/react-util/context";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import RecordingIcon from "@redotech/redo-icons/dot-01.svg";
import DotsGridIcon from "@redotech/redo-icons/dots-grid.svg";
import LinkIcon from "@redotech/redo-icons/link-external-02.svg";
import MicrophoneIcon from "@redotech/redo-icons/microphone-01.svg";
import MicrophoneOffIcon from "@redotech/redo-icons/microphone-off-01.svg";
import PauseCircle from "@redotech/redo-icons/pause-circle_filled.svg";
import PhoneOutgoingIcon from "@redotech/redo-icons/phone-outgoing.svg";
import PhonePauseIcon from "@redotech/redo-icons/phone-pause.svg";
import PhoneXIcon from "@redotech/redo-icons/phone-x.svg";
import VolumeMaxIcon from "@redotech/redo-icons/volume-max.svg";
import VolumeXIcon from "@redotech/redo-icons/volume-x.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { DotsLoading } from "@redotech/redo-web/animations/dots-loading";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { UserImage, UserImageSize } from "@redotech/redo-web/user-image";
import { TimeElapsed } from "@redotech/redo-web/voice/shared/active-call-elapsed-time";
import { PhoneWidgetNumpad } from "@redotech/redo-web/voice/shared/phone-numpad";
import {
  handleInputDeviceChange,
  handleOutputDeviceChange,
  retrieveIODevices,
} from "@redotech/redo-web/voice/shared/utils";
import { sinkPromise } from "@redotech/util/promise";
import {
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useNavigate } from "react-router-dom";
import { TransferPanelV2 } from "./transfer-panel-v2";
import type { ConferenceParticipant } from "./use-voice-actions";
import { VoiceContextV2, type ActiveCallV2 } from "./voice-context-v2";

type ActivePanel = "keypad" | "transfer" | null;

/**
 * In-call section: caller info + (optional) numpad + audio device controls
 * + hold/record/hangup. Mirrors v1's `active-call-view` + `active-call-controls`
 * but consumes `VoiceContextV2`.
 */
export const ActiveCallSectionV2 = memo(function ActiveCallSectionV2({
  call,
}: {
  call: ActiveCallV2;
}) {
  // Keypad and Transfer are mutually exclusive panels — switching to
  // one closes the other so the widget body never tries to render
  // both stacked on top of each other.
  const [panel, setPanel] = useState<ActivePanel>(null);
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }

  const displayName = call.customer?.name ?? call.remoteNumber;
  const showTimer = call.bridged && call.startedAt != null;
  const panelOpen = panel != null;

  return (
    <Flex dir="column" gap="md" p="xl">
      {/* Caller info, ticket link, and participants list are hidden
          while a sub-panel (keypad or transfer) is open — the
          operator's job in those modes is dialing/picking, not
          reading metadata. Restored as soon as the panel closes. */}
      {!panelOpen && (
        <>
          <Flex align="center" dir="column" gap="xs">
            <UserImage
              alt={displayName}
              imageUrl={call.customer?.imageUrl ?? null}
              size={UserImageSize.MEDIUM_SMALL}
            />
            <Text fontSize="xs" textColor="primary">
              {displayName}
            </Text>
            {call.customer?.name && (
              <Text fontSize="xxs" textColor="secondary">
                {call.remoteNumber}
              </Text>
            )}
            {showTimer ? (
              <TimeElapsed startTime={call.startedAt!} />
            ) : (
              <Flex align="center" gap="xs">
                <DotsLoading />
                <Text fontSize="xxs" textColor="secondary">
                  {call.direction === "outbound"
                    ? "Calling..."
                    : "Connecting..."}
                </Text>
              </Flex>
            )}
            {ctx.recording && (
              <Flex align="center" gap="xxs">
                <RecordingIcon style={{ color: "red" }} />
                <Text fontSize="xxs" textColor="error">
                  Recording
                </Text>
              </Flex>
            )}
            {ctx.onHold && (
              <Flex align="center" gap="xxs">
                <PauseCircle />
                <Text fontSize="xxs">On hold</Text>
              </Flex>
            )}
          </Flex>

          <GoToTicketButton />

          <ParticipantsList />
        </>
      )}

      <ActiveCallControlsV2 panel={panel} setPanel={setPanel} />
    </Flex>
  );
});

const GoToTicketButton = memo(function GoToTicketButton() {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }
  const team = useRequiredContext(TeamContext);
  const navigate = useNavigate();
  const supportTicketId = ctx.activeSupportTicketId;
  if (supportTicketId == null) {
    return null;
  }
  return (
    <RedoButton
      hierarchy="secondary"
      IconLeading={LinkIcon}
      onClick={() =>
        navigate(`/stores/${team._id}/support-v2/${supportTicketId}`)
      }
      size="xs"
      text="Go to ticket"
    />
  );
});

const ParticipantsList = memo(function ParticipantsList() {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }
  const { participants, transferRinging } = ctx;

  if (participants.length === 0 && transferRinging == null) {
    return null;
  }

  return (
    <Flex bgColor="secondary" borderRadius="md" dir="column" gap="xs" p="md">
      <Text fontSize="xxs" textColor="secondary">
        On this call
      </Text>
      {participants.map((p) => (
        <ParticipantRow key={p.callControlId} participant={p} />
      ))}
      {transferRinging && (
        <Flex align="center" gap="sm">
          <UserImage
            alt={transferRinging.targetLabel}
            imageUrl={null}
            size={UserImageSize.X_TINY}
          />
          <Flex dir="column" gap="none" grow={1}>
            <Text fontSize="xxs" textColor="primary">
              {transferRinging.targetLabel}
            </Text>
            <Flex align="center" gap="xxs">
              <DotsLoading />
              <Text fontSize="xxs" textColor="secondary">
                Ringing...
              </Text>
            </Flex>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});

const ParticipantRow = memo(function ParticipantRow({
  participant,
}: {
  readonly participant: ConferenceParticipant;
}) {
  const { primary, secondary } = useMemo(() => {
    if (participant.kind === "customer") {
      return {
        primary: participant.name,
        secondary: participant.phoneNumber || "Customer",
      };
    }
    if (participant.kind === "agent") {
      return { primary: participant.name, secondary: "Agent" };
    }
    if (participant.kind === "manager") {
      return {
        primary: participant.name,
        secondary:
          participant.visibility === "private"
            ? "Manager (private)"
            : "Manager",
      };
    }
    return { primary: participant.phoneNumber, secondary: "External" };
  }, [participant]);

  return (
    <Flex align="center" gap="sm">
      <UserImage alt={primary} imageUrl={null} size={UserImageSize.X_TINY} />
      <Flex dir="column" gap="none" grow={1}>
        <Text fontSize="xxs" textColor="primary">
          {primary}
        </Text>
        <Text fontSize="xxs" textColor="secondary">
          {secondary}
        </Text>
      </Flex>
    </Flex>
  );
});

const ActiveCallControlsV2 = memo(function ActiveCallControlsV2({
  panel,
  setPanel,
}: {
  panel: ActivePanel;
  setPanel: (panel: ActivePanel) => void;
}) {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }

  const numpadShowing = panel === "keypad";
  const transferShowing = panel === "transfer";

  const [availableInputDevices, setAvailableInputDevices] = useState<
    MediaDeviceInfo[]
  >([]);
  const [availableOutputDevices, setAvailableOutputDevices] = useState<
    MediaDeviceInfo[]
  >([]);
  const [currentInputDevice, setCurrentInputDevice] = useState<string | null>(
    null,
  );
  const [currentOutputDevice, setCurrentOutputDevice] = useState<string | null>(
    null,
  );
  const [, setAudioStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    sinkPromise(
      retrieveIODevices({}).then((devices) => {
        setAvailableInputDevices(devices.audioInputDevices);
        setAvailableOutputDevices(devices.audioOutputDevices);
        setCurrentInputDevice(devices.currentInputDevice);
        setCurrentOutputDevice(devices.currentOutputDevice);
      }),
    );
  }, []);

  // Bottom row stays put; top row swaps with the active panel
  // (keypad or transfer). Keeps the panel-toggle buttons in stable
  // spots so toggling on/off doesn't shift other controls under the
  // operator's cursor.
  const togglePanel = (target: NonNullable<ActivePanel>) =>
    setPanel(panel === target ? null : target);

  const bottomRow = (
    <Flex gap="sm" justify="space-between">
      <MicControl
        availableInputDevices={availableInputDevices}
        currentInputDevice={currentInputDevice}
        setAudioStream={setAudioStream}
        setCurrentInputDevice={setCurrentInputDevice}
      />
      <CallControlButton
        Icon={PhoneOutgoingIcon}
        label="Transfer"
        onClick={() => togglePanel("transfer")}
        pressed={transferShowing}
      />
      <CallControlButton
        Icon={DotsGridIcon}
        label="Keypad"
        onClick={() => togglePanel("keypad")}
        pressed={numpadShowing}
      />
    </Flex>
  );

  return (
    <Flex bgColor="secondary" borderRadius="md" dir="column" gap="md" p="md">
      {panel === "keypad" ? (
        <PhoneWidgetNumpad itemPressed={(digit) => ctx.sendDtmf(digit)} />
      ) : panel === "transfer" ? (
        <TransferPanelV2 onTransferred={() => setPanel(null)} />
      ) : (
        <Flex gap="sm" justify="space-between">
          <SpeakerControl
            availableOutputDevices={availableOutputDevices}
            currentOutputDevice={currentOutputDevice}
            setCurrentOutputDevice={setCurrentOutputDevice}
          />
          <CallControlButton
            Icon={PhonePauseIcon}
            label="Hold"
            onClick={ctx.toggleHold}
            pressed={ctx.onHold}
          />
          <CallControlButton
            Icon={RecordingIcon}
            label="Record"
            onClick={ctx.toggleRecord}
            pressed={ctx.recording}
          />
        </Flex>
      )}
      {bottomRow}
      <RedoButton
        hierarchy="primary"
        IconTrailing={PhoneXIcon}
        onClick={ctx.hangup}
        text="Hang up"
        theme="destructive"
      />
    </Flex>
  );
});

const MicControl = memo(function MicControl({
  availableInputDevices,
  currentInputDevice,
  setCurrentInputDevice,
  setAudioStream,
}: {
  availableInputDevices: MediaDeviceInfo[];
  currentInputDevice: string | null;
  setCurrentInputDevice: Dispatch<SetStateAction<string | null>>;
  setAudioStream: Dispatch<SetStateAction<MediaStream | null>>;
}) {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }
  const deviceOptions: RedoListItem<MediaDeviceInfo>[] = useMemo(
    () =>
      availableInputDevices.map((device) => ({
        id: device.deviceId,
        value: device,
        type: "text",
        text: device.label,
      })),
    [availableInputDevices],
  );
  const isLockedPrivate = ctx.activeCall?.managerVisibility === "private";
  return (
    <CallControlButton
      deviceOptions={deviceOptions}
      devicePickerLabel="Choose microphone"
      disabled={isLockedPrivate}
      Icon={ctx.muted ? MicrophoneOffIcon : MicrophoneIcon}
      label={isLockedPrivate ? "Muted (private)" : "Mute"}
      onClick={ctx.toggleMute}
      onDeviceSelected={(device) =>
        sinkPromise(
          handleInputDeviceChange({
            deviceId: device.deviceId,
            setCurrentInputDevice,
            setAudioStream,
          }),
        )
      }
      pressed={ctx.muted}
      selectedDeviceId={currentInputDevice ?? undefined}
    />
  );
});

const SpeakerControl = memo(function SpeakerControl({
  availableOutputDevices,
  currentOutputDevice,
  setCurrentOutputDevice,
}: {
  availableOutputDevices: MediaDeviceInfo[];
  currentOutputDevice: string | null;
  setCurrentOutputDevice: Dispatch<SetStateAction<string | null>>;
}) {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }
  const deviceOptions: RedoListItem<MediaDeviceInfo>[] = useMemo(
    () =>
      availableOutputDevices.map((device) => ({
        id: device.deviceId,
        value: device,
        type: "text",
        text: device.label,
      })),
    [availableOutputDevices],
  );
  return (
    <CallControlButton
      deviceOptions={deviceOptions}
      devicePickerLabel="Choose speaker"
      Icon={ctx.deafened ? VolumeXIcon : VolumeMaxIcon}
      label="Speaker"
      onClick={ctx.toggleDeafen}
      onDeviceSelected={(device) =>
        sinkPromise(
          handleOutputDeviceChange({
            deviceId: device.deviceId,
            setCurrentOutputDevice,
          }),
        )
      }
      pressed={ctx.deafened}
      selectedDeviceId={currentOutputDevice ?? undefined}
    />
  );
});

/**
 * One cell of the call-control grid: icon-on-top, label-below.
 * Mirrors the layout of native phone-app call controls so operators
 * don't need to guess what an icon means — the label is always
 * visible.
 *
 * When `deviceOptions` is provided (mic / speaker cells), the label
 * doubles as a device-picker trigger: a chevron sits next to the
 * label text, and clicking the row opens a dropdown of audio devices
 * anchored to the chevron.
 */
const CallControlButton = memo(function CallControlButton({
  Icon,
  label,
  onClick,
  pressed,
  disabled,
  deviceOptions,
  selectedDeviceId,
  onDeviceSelected,
  devicePickerLabel,
}: {
  readonly Icon: ComponentType<{ className?: string }>;
  readonly label: string;
  readonly onClick: () => void;
  readonly pressed?: boolean;
  readonly disabled?: boolean;
  readonly deviceOptions?: readonly RedoListItem<MediaDeviceInfo>[];
  readonly selectedDeviceId?: string;
  readonly onDeviceSelected?: (device: MediaDeviceInfo) => void;
  readonly devicePickerLabel?: string;
}) {
  const [pickerAnchor, setPickerAnchor] = useState<HTMLButtonElement | null>(
    null,
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const hasPicker =
    deviceOptions != null &&
    deviceOptions.length > 0 &&
    onDeviceSelected != null;

  return (
    <Flex align="center" basis="0" dir="column" gap="xxs" grow={1} shrink={0}>
      <RedoButton
        disabled={disabled}
        hierarchy="secondary"
        IconLeading={Icon}
        onClick={onClick}
        pressed={pressed}
        size="sm"
      />
      {hasPicker ? (
        <>
          <button
            aria-label={devicePickerLabel ?? label}
            onClick={() => setPickerOpen((o) => !o)}
            ref={setPickerAnchor}
            style={{
              alignItems: "center",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              display: "flex",
              gap: "var(--redo-spacing-xxs)",
              padding: "var(--redo-spacing-none)",
            }}
            type="button"
          >
            <Text fontSize="xxs" textColor="secondary">
              {label}
            </Text>
            <ChevronDownIcon style={{ height: 10, width: 10 }} />
          </button>
          <RedoListDropdown
            dropdownAnchor={pickerAnchor}
            dropdownOpen={pickerOpen}
            items={[...deviceOptions]}
            itemSelected={({ item }) => {
              onDeviceSelected?.(item.value);
              setPickerOpen(false);
            }}
            selectedItems={selectedDeviceId}
            setDropdownOpen={setPickerOpen}
            size="xs"
          />
        </>
      ) : (
        <Text fontSize="xxs" textColor="secondary">
          {label}
        </Text>
      )}
    </Flex>
  );
});
